import { Link } from "react-router-dom"
import {
  Calendar,
  Phone,
  MapPin,
  Clock,
  Building,
  Stethoscope,
  Bed,
  Microscope,
  Truck,
  Coffee,
  Wifi,
  ChevronRight,
  CheckCircle,
  Star,
  HeartPulse,
  Brain,
  Bone,
  Baby,
  User,
  Pill,
} from "lucide-react"
import SEO from "../SEO"
import { Button } from "../../components/ui/button"
import { Card, CardContent, CardFooter } from "../../components/ui/card"
import { Badge } from "../../components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs"
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "../../components/ui/carousel"

export default function MadinagudaBranch() {
  const madinagudaSchema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": "Pranaam Hospitals – Madinaguda",
    "url": "https://pranaam.pranaamhospitals.in/locations/madinaguda",
    "logo": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/logo.png",
    "description": "Pranaam Hospitals Madinaguda is a NABH-accredited multi-specialty hospital providing comprehensive healthcare, diagnostics, and 24/7 emergency services near Miyapur, Hyderabad.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Madinaguda",
      "addressRegion": "Hyderabad",
      "addressCountry": "IN"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "contactType": "appointments",
        "telephone": "+91-99660-60000",
        "availableLanguage": ["English", "Telugu", "Hindi"]
      },
      {
        "@type": "ContactPoint",
        "contactType": "emergency",
        "telephone": "+91-98485-98285",
        "availableLanguage": ["English", "Telugu", "Hindi"]
      }
    ],
    "parentOrganization": {
      "@type": "MedicalOrganization",
      "name": "Pranaam Hospitals",
      "url": "https://pranaam.pranaamhospitals.in/"
    }
  },

  {
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pranaam.pranaamhospitals.in/locations/madinaguda"
    },
    "headline": "Pranaam Hospitals Madinaguda",
    "description": "Visit Pranaam Hospitals Madinaguda for expert medical care, advanced diagnostics, specialist consultations, and emergency services in Hyderabad.",
    "author": {
      "@type": "Organization",
      "name": "Pranaam Hospitals"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Pranaam Hospitals",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/logo.png"
      }
    },
    "datePublished": "2015-01-01",
    "dateModified": "2025-04-01",
    "keywords": [
      "Pranaam Hospitals Madinaguda",
      "hospital near Miyapur",
      "emergency hospital Madinaguda",
      "best multi specialty hospital Hyderabad"
    ]
  }
];


  return (
    <>
    <SEO
  title="Pranaam Hospitals Madinaguda | Multi-Specialty Hospital Near Miyapur"
  description="Pranaam Hospitals Madinaguda offers expert doctors, advanced diagnostics, and 24/7 emergency care. Trusted multi-specialty hospital near Miyapur, Hyderabad."
  canonical="https://pranaam.pranaamhospitals.in/locations/madinaguda"
  robots="index, follow"
  author="Pranaam Hospitals"
  publisher="Pranaam Hospitals"
  keywords="Pranaam Hospitals Madinaguda, hospital near Miyapur Hyderabad,
            multi specialty hospital Madinaguda, emergency hospital Madinaguda,
            best doctors near Miyapur, NABH hospital Madinaguda,
            diagnostics Madinaguda Hyderabad"
  schema={madinagudaSchema}
/>

    <div className="min-h-screen bg-white">
      <main>
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="container px-4 mx-auto">
            <div className="grid items-center grid-cols-1 gap-12 md:grid-cols-2">
              <div className="max-w-lg">
                <Badge className="mb-4 px-3 py-1 bg-primary/10 text-primary border-primary/20">
                  <Building className="w-4 h-4 mr-1" />
                  Pranaam Hospitals Branch
                </Badge>
                <h1 className="mb-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                  Madinaguda <span className="text-primary">Branch</span>
                </h1>
                <p className="mb-8 text-lg text-gray-700">
                  Our flagship hospital in Madinaguda offers comprehensive healthcare services with state-of-the-art
                  facilities and expert medical professionals dedicated to your well-being.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button size="lg" className="gap-2 shadow-md" asChild>
                 <a
                    href="https://pranaamhospitals.com/apps/pranaam/patient/?routeName=doctors&refId=otp&visitType=T&treatmentType=1&deepLink=Y&consultType=001"
                    rel="noopener noreferrer"
                  >
                  Book an Appointment</a>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="gap-2 border-primary text-primary hover:bg-primary hover:text-white"
                    asChild
                  >
                    <Link to="tel:+919966060000">
                      <Phone className="w-5 h-5" />
                      Call Us
                    </Link>
                  </Button>
                </div>
              </div>
              <div className="relative">
                <div className="relative h-64 overflow-hidden rounded-lg shadow-xl md:h-96">
                  <img
                    src="/images/PranaamBuilding.png"
                    alt="Pranaam Hospitals Madinaguda Branch"
                    className="w-full h-full object-cover"
                  />
               
                </div>
                {/* Material Design decorative elements */}
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-primary/10 rounded-full"></div>
                <div className="absolute -top-6 -left-6 w-24 h-24 bg-primary/10 rounded-full"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Info Section */}
        <section className="py-8 bg-white">
          <div className="container px-4 mx-auto">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <Card className="transition-all hover:shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-2">
                    <div className="p-3 rounded-full bg-primary/10">
                      <MapPin className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-bold">Location</h3>
                  </div>
                  <p className="text-gray-700">
                    1-58/6/40 & 41, Mythri Nagar, Madinaguda, Miyapur, Hyderabad – 500050, Telangana, India
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    <span className="font-medium">Landmarks:</span> Near Pai Store & Sujana Super Market
                  </p>
                </CardContent>
              </Card>

              <Card className="transition-all hover:shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-2">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Phone className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-bold">Contact</h3>
                  </div>
                  <p className="text-gray-700">
                    <span className="block">Landline: +91 40 2304 2777 / 3777 / 3509 4777</span>
                    <span className="block">Mobile: +91 99660 60000 / +91 98485 98285</span>
                    <span className="block">Email: info@pranaamhospitals.in</span>
                  </p>
                </CardContent>
              </Card>

              <Card className="transition-all hover:shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-2">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Clock className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-bold">Hours</h3>
                  </div>
                  <p className="text-gray-700">
                    <span className="block">Emergency: 24/7</span>
                    <span className="block">OPD: 8:00 AM - 8:00 PM</span>
                    <span className="block">All days of the week</span>
                  </p>
                </CardContent>
              </Card>

              <Card className="transition-all hover:shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-2">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Truck className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-bold">Emergency</h3>
                  </div>
                  <p className="text-gray-700">
                    <span className="block">24/7 Emergency Services</span>
                    <span className="block">Ambulance: +91 98485 98285</span>
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* About Branch Section */}
        <section className="py-12 bg-gray-50">
          <div className="container px-4 mx-auto">
            <div className="grid items-center grid-cols-1 gap-12 md:grid-cols-2">
              <div>
                <Badge className="mb-2">About Our Branch</Badge>
                <h2 className="mb-6 text-3xl font-bold md:text-4xl">
                  Excellence in Healthcare at <span className="text-primary">Madinaguda</span>
                </h2>
                <p className="mb-6 text-gray-700">
                  Established in 2002, our Madinaguda branch is the flagship hospital of Pranaam Hospitals. Spread
                  across 60,000 sq. ft., this state-of-the-art facility is equipped with the latest medical technology
                  and staffed by experienced healthcare professionals.
                </p>
                <p className="mb-6 text-gray-700">
                  The hospital features 125 beds, including specialized ICU units, modern operation theaters, advanced
                  diagnostic facilities, and dedicated departments for various medical specialties. Our Madinaguda
                  branch has been recognized for excellence in healthcare delivery and has received several
                  accreditations for maintaining high standards of patient care and safety.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0" />
                    <span>NABH Accredited</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0" />
                    <span>ISO 9001 Certified</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0" />
                    <span>125 Beds</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0" />
                    <span>24/7 Emergency Care</span>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="relative h-64 overflow-hidden rounded-lg shadow-xl md:h-96">
                  <img
                    src="/images/PranaamBuilding.png"
                    alt="Pranaam Hospitals Madinaguda Building"
                    className="w-full h-full object-cover"
                  />
                  
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Facilities & Infrastructure */}
        <section className="py-12 bg-white">
          <div className="container px-4 mx-auto">
            <div className="flex flex-col items-center mb-12 text-center">
              <Badge className="mb-2">Our Facilities</Badge>
              <h2 className="mb-4 text-3xl font-bold md:text-4xl">
                State-of-the-Art <span className="text-primary">Infrastructure</span>
              </h2>
              <p className="max-w-2xl text-gray-700">
                Our Madinaguda branch is equipped with advanced medical technology and modern facilities to provide
                comprehensive healthcare services.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <Card className="transition-all hover:shadow-md hover:translate-y-[-5px] duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Bed className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold">Inpatient Facilities</h3>
                  </div>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 mt-0.5 text-primary shrink-0" />
                      <span>125 beds including private, semi-private, and general wards</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 mt-0.5 text-primary shrink-0" />
                      <span>20-bed ICU with advanced monitoring systems</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 mt-0.5 text-primary shrink-0" />
                      <span>10-bed Neonatal ICU</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 mt-0.5 text-primary shrink-0" />
                      <span>8-bed Pediatric ICU</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 mt-0.5 text-primary shrink-0" />
                      <span>12-bed Cardiac Care Unit</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="transition-all hover:shadow-md hover:translate-y-[-5px] duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Stethoscope className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold">Surgical Facilities</h3>
                  </div>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 mt-0.5 text-primary shrink-0" />
                      <span>6 modern operation theaters</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 mt-0.5 text-primary shrink-0" />
                      <span>Advanced laparoscopic surgery equipment</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 mt-0.5 text-primary shrink-0" />
                      <span>Robotic surgery capabilities</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 mt-0.5 text-primary shrink-0" />
                      <span>Dedicated cardiac catheterization lab</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 mt-0.5 text-primary shrink-0" />
                      <span>Post-operative recovery rooms</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="transition-all hover:shadow-md hover:translate-y-[-5px] duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Microscope className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold">Diagnostic Services</h3>
                  </div>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 mt-0.5 text-primary shrink-0" />
                      <span>128-slice CT scanner</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 mt-0.5 text-primary shrink-0" />
                      <span>1.5T MRI machine</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 mt-0.5 text-primary shrink-0" />
                      <span>Digital X-ray and ultrasound</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 mt-0.5 text-primary shrink-0" />
                      <span>Fully equipped pathology lab</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 mt-0.5 text-primary shrink-0" />
                      <span>ECG, EEG, and other cardiac diagnostics</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Location Map */}
        <section className="py-12 bg-gray-50">
          <div className="container px-4 mx-auto">
            <div className="flex flex-col items-center mb-12 text-center">
              <Badge className="mb-2">Find Us</Badge>
              <h2 className="mb-4 text-3xl font-bold md:text-4xl">
                Our <span className="text-primary">Location</span>
              </h2>
              <p className="max-w-2xl text-gray-700">
                Our Madinaguda branch is conveniently located and easily accessible from all parts of the city.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              <div className="h-80 bg-gray-200 rounded-lg overflow-hidden">
                <iframe
                  src="https://www.google.com/maps?q=1-58/6/40+%26+41,+Mythri+Nagar,+Madinaguda,+Miyapur,+Hyderabad,+Telangana+500050&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold">Pranaam Hospitals Madinaguda</h3>
                  <p className="text-muted-foreground">
                    1-58/6/40 & 41, Mythri Nagar, Madinaguda, Miyapur, Hyderabad – 500050, Telangana, India
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    <span className="font-medium">Landmarks:</span> Near Pai Store & Sujana Super Market
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <Phone className="w-4 h-4 text-primary" />
                    <span>Landline: +91 40 2304 2777 / 3777 / 3509 4777</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Phone className="w-4 h-4 text-primary" />
                    <span>Mobile: +91 99660 60000 / +91 98485 98285</span>
                  </div>
                  <div className="mt-4">
                    <Button variant="outline" size="sm" className="gap-2" asChild>
                      <Link to="https://maps.google.com" target="_blank" rel="noopener noreferrer">
                        <MapPin className="w-4 h-4" />
                        Get Directions
                      </Link>
                    </Button>
                  </div>
                </div>

                <div className="p-4 bg-white rounded-lg shadow-sm">
                  <h3 className="mb-3 text-lg font-bold">How to Reach Us</h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <div className="p-1 mt-0.5 rounded-full bg-primary/10">
                        <MapPin className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">By Metro</p>
                        <p className="text-sm text-gray-600">
                          Nearest Metro Station: Madinaguda (5 minutes walking distance)
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="p-1 mt-0.5 rounded-full bg-primary/10">
                        <MapPin className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">By Bus</p>
                        <p className="text-sm text-gray-600">
                          Multiple bus routes stop at Madinaguda Bus Stop (2 minutes walking distance)
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="p-1 mt-0.5 rounded-full bg-primary/10">
                        <MapPin className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">By Car</p>
                        <p className="text-sm text-gray-600">
                          Ample parking space available. Easy access from ORR and Miyapur Road.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="py-12 bg-primary text-white">
          <div className="container px-4 mx-auto">
            <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
              <div className="text-center md:text-left">
                <h2 className="mb-2 text-2xl font-bold md:text-3xl">Need Medical Assistance?</h2>
                <p className="text-primary-foreground/90">Schedule an appointment at our Madinaguda branch today.</p>
              </div>
              <div className="flex flex-wrap gap-4">
                <Button variant="secondary" size="lg" className="gap-2 shadow-md" asChild>
                  <a
                    href="https://pranaamhospitals.com/apps/pranaam/patient/?routeName=doctors&refId=otp&visitType=T&treatmentType=1&deepLink=Y&consultType=001"
                    rel="noopener noreferrer"
                  >
                  Book an Appointment</a>
                </Button>
                <Button variant="secondary" size="lg" className="gap-2 shadow-md" asChild>
                  <Link to="tel:+919966060000">
                    <Phone className="w-5 h-5" />
                    Call Us Now
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
    </>
  )
}